import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/sonner'
import { ApiError, api, type Case } from '@/services/api'
import { casesQueryKey } from './useCases'

/** Delete-case mutation with optimistic removal from the cached list. */
export function useDeleteCase(opts?: { onSuccess?: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (caseData: Case) => api.cases.delete(caseData.id),
    onSuccess: (_, caseData) => {
      queryClient.setQueryData<Case[]>(casesQueryKey, prev =>
        prev ? prev.filter(c => c.id !== caseData.id) : prev
      )
      queryClient.removeQueries({ queryKey: ['case', caseData.id] })
      void queryClient.invalidateQueries({ queryKey: casesQueryKey })
      toast.success(t('cases.delete.successTitle'), {
        description: t('cases.delete.successDesc', { name: caseData.name }),
      })
      opts?.onSuccess?.()
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : t('cases.delete.failedTitle')
      toast.error(t('cases.delete.failedTitle'), { description: msg })
    },
  })

  return { deleteCase: mutation.mutate, deleting: mutation.isPending }
}
